import { CopyButton } from "@components/shared/CopyButton";
import { ALL_CAMPAIGN_CANDIDATES_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { interviewStepBuckets, type ProcessStep, type SelectionStep } from "@lib/selectionSteps";
import { STAGE_LABELS } from "@lib/types";
import { useCallback, useMemo } from "react";
import { useQuery } from "urql";

interface SnapshotCandidate {
  id: string;
  stage: string;
  candidateSelectionSteps: SelectionStep[];
}

const FORWARD_STAGES = [
  "longlist",
  "outreach",
  "assessment",
  "shortlist",
  "interview",
  "offer",
  "placed",
] as const;

const TERMINAL_STAGES = ["rejected", "withdrawn"] as const;

function escapePipelineHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function CandidatePipelineSnapshot({ campaignId }: { campaignId: string }) {
  const [{ data, fetching }, reexecute] = useQuery({
    query: ALL_CAMPAIGN_CANDIDATES_QUERY,
    variables: { id: campaignId },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const candidates: SnapshotCandidate[] = data?.campaign?.campaignCandidates ?? [];
  const processSteps: ProcessStep[] = data?.campaign?.brief?.selectionProcess?.steps ?? [];

  const counts = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const c of candidates) {
      totals[c.stage] = (totals[c.stage] ?? 0) + 1;
    }
    return totals;
  }, [candidates]);

  const visibleStages = useMemo(() => {
    const stages: string[] = [...FORWARD_STAGES];
    for (const s of TERMINAL_STAGES) {
      if ((counts[s] ?? 0) > 0) stages.push(s);
    }
    return stages;
  }, [counts]);

  const stepBuckets = useMemo(
    () => interviewStepBuckets(candidates, processSteps),
    [candidates, processSteps],
  );

  const buildCopyContent = useCallback(() => {
    const rows = visibleStages.map((stage) => {
      const label = STAGE_LABELS[stage] ?? stage;
      const count = counts[stage] ?? 0;
      return { label, count };
    });
    const text = ["Stage\tCount", ...rows.map((r) => `${r.label}\t${r.count}`)].join("\n");
    const html = [
      "<table>",
      "<thead><tr><th>Stage</th><th>Count</th></tr></thead>",
      "<tbody>",
      ...rows.map(
        (r) => `<tr><th scope="row">${escapePipelineHtml(r.label)}</th><td>${r.count}</td></tr>`,
      ),
      "</tbody>",
      "</table>",
    ].join("");
    return { html, text };
  }, [visibleStages, counts]);

  if (fetching && candidates.length === 0) {
    return (
      <div
        className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
        data-testid="pipeline-snapshot-loading"
      >
        <h3 className="text-sm font-medium text-text-secondary">Candidate pipeline</h3>
        <div className="mt-3 flex gap-2">
          {FORWARD_STAGES.map((s) => (
            <div key={s} className="h-14 w-20 animate-pulse rounded-md bg-card-alt" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-secondary">Candidate pipeline</h3>
        {candidates.length > 0 && (
          <CopyButton
            ariaLabel="Copy pipeline snapshot"
            withLabel
            size={12}
            getContent={buildCopyContent}
          />
        )}
      </div>
      {candidates.length === 0 ? (
        <p className="mt-2 text-sm text-text-muted">No candidates yet</p>
      ) : (
        <ol className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(96px,1fr))] gap-2">
          {visibleStages.map((stage) => {
            const count = counts[stage] ?? 0;
            return (
              <li
                key={stage}
                className="rounded-md border border-border bg-card-alt px-3 py-2 text-center"
                data-testid={`snapshot-stage-${stage}`}
              >
                <div className="text-xs font-medium uppercase tracking-wide text-text-muted">
                  {STAGE_LABELS[stage] ?? stage}
                </div>
                <div className="mt-1 text-xl font-semibold tabular-nums text-text-primary">
                  {count}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {(counts.interview ?? 0) > 0 && stepBuckets.length > 0 && (
        <div className="mt-4 border-t border-border pt-3" data-testid="snapshot-interview-steps">
          <div className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Interview steps
          </div>
          <ol className="mt-2 grid grid-cols-[repeat(auto-fit,minmax(96px,1fr))] gap-2">
            {stepBuckets.map((bucket) => (
              <li
                key={bucket.key}
                className="rounded-md border border-border bg-card-alt px-3 py-2 text-center"
                data-testid={`snapshot-interview-step-${bucket.key}`}
              >
                <div className="text-xs font-medium text-text-muted">{bucket.label}</div>
                <div className="mt-1 text-xl font-semibold tabular-nums text-text-primary">
                  {bucket.count}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
